import React, { useEffect, useState } from "react";
import { Alert, IconButton, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import LinkIcon from '@mui/icons-material/Link';
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { TextLoader } from "components/apiHandling/TextLoader";
import APIRequest from "system/APIRequest";
import { InfegyAccount } from "system/InfegyUser";
import { BillingInvoice } from "../types/BillingInvoice";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import textColumn from "components/tables/columns/TextColumn";
import dateColumn from "components/tables/columns/DateColumn";
import currencyColumn from "components/tables/columns/CurrencyColumn";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

interface InvoicesProps {
  account: InfegyAccount;
  visible: boolean;
}

export default function Invoices(props: InvoicesProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [billingInvoices, setBillingInvoices] = useState<Array<BillingInvoice>>([]);
  const [currency, setCurrency] = useState<string>("");

  async function load(account: InfegyAccount) {
    setIsLoading(true);
    setLoadError("");
    setBillingInvoices([]);
    setCurrency("");

    const queryParams = {
      admin: "1",
      schema: "1",
      q: JSON.stringify([{
        id: "account_id",
        op: "=",
        value: account.id
      }, {
        id: "status",
        op: "!=",
        value: "draft"
      }, {
        id: "status",
        op: "!=",
        value: "void"
      }, {
        id: "type",
        op: "=",
        value: "api-usage"
      }]),
      sort: "due_at",
      sort_order: "desc"
    }
    try {
      const response: any = await APIRequest(`obj/billing-invoice`, queryParams);
      const billingInvoices: Array<BillingInvoice> = response.output;

      let currency = "";
      if (billingInvoices.length > 0) {
        currency = billingInvoices[0].data.currency;
      }

      setBillingInvoices(billingInvoices);
      setCurrency(currency);
    } catch (error: any) {
      if (error.code) {
        // This was an error from the API.  Display it as such.
        setLoadError(`${error.message}: ${error.server_message}`);
      } else {
        // This was an error from the UI.
        setLoadError(`${i18n.t("api-usage-page.error-loading")} ${error}`);
      }
    }
    setIsLoading(false);
  }

  function openBillingInvoiceTab(invoice: BillingInvoice) {
    let url = `/billing/order/view/${invoice.billing_order_id}`;
    if (window.location.hostname === "localhost") {
      url = `https://starscape-staging.infegy.com/billing/order/view/${invoice.billing_order_id}`;
    }
    window.open(url, '_blank', 'noopener, noreferrer');
  }

  function downloadBillingInvoicePdf(invoice: BillingInvoice) {
    let pfdUrl = `/billing/invoice/${invoice.id}?pdf=1`;
    if (window.location.hostname === "localhost") {
      pfdUrl = `https://starscape-staging.infegy.com/billing/invoice/${invoice.id}?pdf=1`;
    }
    const link = document.createElement("a");
    link.href = pfdUrl;
    link.setAttribute("download", `${invoice.number}.pdf`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 5000);
  }

  useEffect(() => {
    load(props.account);
  }, [props.account]);

  const columns: Array<GridColDef> = [{
    field: "number",
    flex: 1,
    headerName: i18n.t("invoices.columns.number"),
    ...textColumn()
  }, {
    field: "service_from",
    flex: 1,
    headerName: i18n.t("invoices.columns.service-from"),
    valueGetter: (value: any, row: BillingInvoice, params: GridColDef) => {
      return row.data.service_from;
    },
    ...dateColumn()
  }, {
    field: "service_to",
    flex: 1,
    headerName: i18n.t("invoices.columns.service-to"),
    valueGetter: (value: any, row: BillingInvoice, params: GridColDef) => {
      return row.data.service_to
    },
    ...dateColumn()
  }, {
    field: "value",
    flex: 1,
    headerName: i18n.t("invoices.columns.price"),
    ...currencyColumn({
      format: {currency: currency}
    }),
    valueGetter: (value: any, row: BillingInvoice, params: GridColDef) => {
      return row.value;
    },
  }, {
    field: "resources",
    flex: 1,
    headerName: i18n.t("invoices.columns.resources"),
    renderCell: (params: GridRenderCellParams) => {
      return (
        <>
          <Tooltip title={i18n.t("invoices.view-link-tooltip")}>
            <IconButton onClick={() => openBillingInvoiceTab(params.row)}>
              <LinkIcon sx={{color: theme.colors.primary.main}} />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("invoices.download-pdf-tooltip")}>
            <IconButton onClick={() => downloadBillingInvoicePdf(params.row)}>
              <PictureAsPdfIcon sx={{color: theme.colors.primary.main}} />
            </IconButton>
          </Tooltip>
        </>
      )
    }
  }];

  return (
    <Stack
      sx={{
        display: props.visible ? "auto" : "none",
        gap: 1
      }}>
      {isLoading &&
        <TextLoader />
      }
      {!isLoading &&
        <>
          <Typography variant="h3">{i18n.t("invoices.invoices-heading")}</Typography>
          {loadError &&
            <Alert severity="error" variant="outlined">{loadError}</Alert>
          }
          {!loadError &&
            <>
              {billingInvoices.length === 0 &&
                <Alert severity="info" variant="outlined">{i18n.t("invoices.no-invoices-info")}</Alert>
              }
              {billingInvoices.length > 0 &&
                <Paper>
                  {props.visible &&
                    <StripedDataGrid
                      columns={columns}
                      rows={billingInvoices}
                    />
                  }
                </Paper>
              }
            </>
          }
        </>
      }
    </Stack>
  );
}
